
/**
 * Safe Storage Utility
 * 
 * Provides a safer wrapper around localStorage with error handling,
 * quota management, and fallbacks for incognito mode or disabled storage.
 */

// In-memory fallback when localStorage is unavailable
const memoryFallback = new Map<string, string>();

// Flag to track localStorage availability
let isLocalStorageAvailable = true;

// Test localStorage availability
try {
  const testKey = '__storage_test__';
  localStorage.setItem(testKey, 'test');
  localStorage.removeItem(testKey);
} catch (e) {
  isLocalStorageAvailable = false;
  console.warn('⚠️ localStorage is not available, using in-memory fallback');
}

/**
 * Safely get an item from storage with error handling
 */
const get = (key: string): string | null => {
  try {
    if (!isLocalStorageAvailable) {
      return memoryFallback.get(key) || null;
    }
    return localStorage.getItem(key);
  } catch (e) {
    console.warn(`Failed to get item from storage: ${key}`, e);
    return memoryFallback.get(key) || null;
  }
};

/**
 * Safely set an item in storage with error handling
 * @returns boolean success flag
 */
const set = (key: string, value: string): boolean => {
  try {
    if (!isLocalStorageAvailable) {
      memoryFallback.set(key, value);
      return true;
    }
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn(`Failed to set item in storage: ${key}`, e);
    // Try to use memory fallback on failure
    try {
      memoryFallback.set(key, value);
    } catch {
      // If everything fails, we simply can't store
      return false;
    }
    return false;
  }
};

/**
 * Safely remove an item from storage with error handling
 */
const remove = (key: string): boolean => {
  try {
    if (!isLocalStorageAvailable) {
      return memoryFallback.delete(key);
    }
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    console.warn(`Failed to remove item from storage: ${key}`, e);
    try {
      return memoryFallback.delete(key);
    } catch {
      return false;
    }
  }
};

/**
 * Safely clear all storage with error handling
 */
const clear = (): boolean => {
  try {
    if (!isLocalStorageAvailable) {
      memoryFallback.clear();
      return true;
    }
    localStorage.clear();
    memoryFallback.clear(); // Also clear fallback just in case
    return true;
  } catch (e) {
    console.warn('Failed to clear storage', e);
    try {
      memoryFallback.clear();
    } catch {
      // Silent fail
    }
    return false;
  }
};

/**
 * Get all storage keys matching a filter function
 */
const filterKeys = (predicate: (key: string) => boolean): string[] => {
  try {
    if (!isLocalStorageAvailable) {
      return Array.from(memoryFallback.keys()).filter(predicate);
    }
    
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && predicate(key)) {
        keys.push(key);
      }
    }
    
    return keys;
  } catch (e) {
    console.warn('Failed to filter storage keys', e);
    
    // Fallback to memory store
    try {
      return Array.from(memoryFallback.keys()).filter(predicate);
    } catch {
      return [];
    }
  }
};

/**
 * Check if storage is available in the current environment
 */
const isAvailable = (): boolean => {
  return isLocalStorageAvailable;
};

// Export the safe storage API
const safeStorage = {
  get,
  set,
  remove,
  clear,
  filterKeys,
  isAvailable,
};

export default safeStorage;
